import { attrs, cx, html, raw, type Html, type Part } from "../html.ts";
import { STATUS, type RunStatus } from "../status.ts";
import { button } from "./actions.ts";
import { command } from "./forms.ts";

export interface ToastProps {
  id?: string;
  status: RunStatus;
  title: string;
  body?: Part;
  actions?: Part;
  dismissible?: boolean;
}

/** bt-toast. Failures use role="alert"; everything else is a polite status. */
export function toast(props: ToastProps): Html {
  const s = STATUS[props.status];
  return html`<div${attrs({ id: props.id, class: cx("bt-toast", s.toastClass), role: props.status === "failed" ? "alert" : "status" })}><span class="bt-glyph ${s.glyphClass}" aria-hidden="true"></span><div><div class="bt-toast__title">${props.title}</div>${props.body !== undefined ? html`<div class="bt-toast__body">${props.body}</div>` : ""}${props.actions !== undefined ? html`<div class="bt-toast__actions">${props.actions}</div>` : ""}</div>${props.dismissible === false ? "" : button({ label: "Dismiss", variant: "ghost", size: "sm", icon: "close", iconOnly: true, ariaLabel: "Dismiss", attrs: { "data-dismiss-toast": props.id } })}</div>`;
}

/** The empty formation: emblem heads as outlines with one orange antenna dot. Not the mascots. */
export const EMPTY_FORMATION_ART: Html = raw(
  '<svg class="bt-empty__art" viewBox="0 0 96 80" aria-hidden="true" focusable="false"><rect class="o" x="30" y="30" width="36" height="34" rx="10"/><rect class="o" x="6" y="38" width="28" height="26" rx="8"/><rect class="o" x="62" y="38" width="28" height="26" rx="8"/><circle class="a" cx="48" cy="20" r="5"/><rect class="v" x="39" y="42" width="18" height="6" rx="3"/></svg>',
);

export interface EmptyStateProps {
  title: string;
  /** What is missing and what to do about it. */
  body: Part;
  /** A copyable next step, shown as a Command. */
  command?: string;
  actions?: Part;
  art?: boolean;
  /** A filtered empty offers this. */
  clearFilters?: Part;
}

/** bt-empty: always offers the next action. */
export function emptyState(props: EmptyStateProps): Html {
  return html`<div class="bt-empty">${props.art === false ? "" : EMPTY_FORMATION_ART}<h2 class="bt-empty__title">${props.title}</h2><p class="bt-empty__body">${props.body}</p>${props.command ? command({ text: props.command }) : ""}${props.actions !== undefined || props.clearFilters !== undefined ? html`<div class="bt-empty__actions">${props.clearFilters ?? ""}${props.actions ?? ""}</div>` : ""}</div>`;
}

export interface ErrorStateProps {
  title: string;
  /** What happened. */
  what: Part;
  /** What it means for the run. */
  meaning: Part;
  /** What to do. */
  next: Part;
  actions?: Part;
  /** A request id or error code, in mono. */
  code?: string;
  inline?: boolean;
  /** Only for something needing action now. Adds role="alert". */
  urgent?: boolean;
}

/** bt-error: what happened, then (bold) what it means for the run, then what to do. */
export function errorState(props: ErrorStateProps): Html {
  return html`<div${attrs({ class: cx("bt-error", props.inline && "bt-error--inline"), role: props.urgent ? "alert" : undefined })}><span class="bt-glyph bt-glyph--failed" aria-hidden="true"></span><h2 class="bt-error__title">${props.title}</h2><p class="bt-error__body">${props.what} <strong>${props.meaning}</strong> ${props.next}</p>${props.actions !== undefined || props.code ? html`<div class="bt-error__actions">${props.actions ?? ""}${props.code ? html`<span class="bt-error__code">${props.code}</span>` : ""}</div>` : ""}</div>`;
}

export interface SkeletonProps {
  /** Text lines. Last line is shorter. */
  lines?: number;
  title?: boolean;
  avatar?: boolean;
  pill?: boolean;
  label?: string;
}

/** bt-skel, shaped like the content. Show it only after 300ms (see `delayedFlag`). */
export function skeleton(props: SkeletonProps = {}): Html {
  const lines = Array.from({ length: props.lines ?? 0 }, () => html`<span class="bt-skel bt-skel--text"></span>`);
  return html`<div${attrs({ role: "group", "aria-busy": "true", "aria-label": props.label ?? "Loading" })}>${props.title ? html`<span class="bt-skel bt-skel--title"></span>` : ""}${props.pill ? html`<span class="bt-skel bt-skel--pill"></span>` : ""}${props.avatar ? html`<span class="bt-skel bt-skel--avatar"></span>` : ""}${lines}</div>`;
}
